import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MakecodeIframe, Container, ButtonDownloadContainer } from './styles';
import IActivityProps from '../../../../dtos/IActivitiesProps';
import { ResourceType, StepType } from '../../../../utils/enums';
import IStepProps from '../../../../dtos/IStepsProps';
import PrimaryButton from '../../../../components/Buttons/PrimaryButton';

type FrameState =
  | 'loading'
  | 'no-project'
  | 'opening-project'
  | 'project-open'
  | 'closing-project';

interface MakeCodeFrameState {
  frameState: FrameState;
  loadPercent?: number;
  workspaceReady: boolean;
  pendingShare: boolean;
}

interface IComponentProps {
  currentActivity: IActivityProps;
  currentStep: IStepProps;
}

const MakecodeFrame: React.FC<IComponentProps> = ({ currentStep: step }) => {
  const iFrameRef = useRef<HTMLIFrameElement>(null);
  const [makecodeUrl, setMakecodeUrl] = useState(' ');
  const [webUsbError, setWebUsbError] = useState(false);
  const [isProjectLoaded, setIsProjectLoaded] = useState(false);
  const [urlCodeTamplate, setUrlCodeTemplate] = useState<string>();
  const { t } = useTranslation();

  const loadTutorialLink = useCallback(
    async (currentStep: IStepProps) => {
      if (!currentStep) return;
      if (currentStep && step.resourse_type === ResourceType.MAKECODE)
        if (currentStep) setMakecodeUrl(currentStep.resource_url);
    },
    [step],
  );

  useEffect(() => {
    if (step) {
      if (step.optional_resource_url) {
        setUrlCodeTemplate(step.optional_resource_url);
      }
    }
  }, [step]);

  useEffect(() => {
    if (step) loadTutorialLink(step);
  }, [loadTutorialLink, step]);

  useEffect(() => {
    const messageHandler = (event: MessageEvent): void => {
      // Verificar se a mensagem vem do iframe do MakeCode
      if (
        iFrameRef.current &&
        event.source === iFrameRef.current.contentWindow
      ) {
        console.log('Mensagem do MakeCode:', event.data);

        // Detectar quando o projeto está carregado
        if (
          event.data.type === 'status' &&
          event.data.state === 'project-open'
        ) {
          setIsProjectLoaded(true);
        }

        // Detectar erros relacionados ao WebUSB
        if (
          event.data.type === 'error' &&
          event.data.message &&
          event.data.message.includes('WebUSB is disabled')
        ) {
          setWebUsbError(true);
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, []);

  // Função para forçar download do arquivo .hex
  const handleForceHexDownload = (): void => {
    if (!iFrameRef.current || !iFrameRef.current.contentWindow) {
      console.error('Iframe não encontrado');
      return;
    }

    // Solicitar download do arquivo .hex em vez de tentar usar WebUSB
    iFrameRef.current.contentWindow.postMessage(
      {
        type: 'pxthost',
        action: 'downloadsourcefile',
        content: {
          name: 'microbit-project',
          download: true,
          format: 'hex', // Explicitamente solicitando o formato .hex
        },
      },
      '*',
    );
  };

  const handleDownloadCodeTemplate = useCallback((): void => {
    if (!urlCodeTamplate) return;
    const a = document.createElement('a');
    a.href = urlCodeTamplate;
    a.download = urlCodeTamplate.split('/').pop() || 'template.hex';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [urlCodeTamplate]);

  return (
    <Container>
      {step && (
        <>
          <MakecodeIframe>
            <iframe
              src={`${makecodeUrl}?controller=1&noproject=1&nocookiebanner=1&ws=browser&origin=${encodeURIComponent(
                window.location.origin,
              )}`}
              title="test pxt"
              ref={iFrameRef}
              allow="usb"
            />
          </MakecodeIframe>
          {urlCodeTamplate && (
            <ButtonDownloadContainer>
              <PrimaryButton onClick={() => handleDownloadCodeTemplate()}>
                {t('buttons.download_code_button')}
              </PrimaryButton>
            </ButtonDownloadContainer>
          )}
          {webUsbError && (
            <div
              style={{
                margin: '15px 0',
                padding: '10px',
                backgroundColor: '#ff9800',
                border: '1px solid #ff9800',
                borderRadius: '4px',
              }}
            >
              <h4>Problemas com WebUSB detectados</h4>
              <p>
                O navegador está bloqueando o acesso direto ao micro:bit via
                USB. Opções:
              </p>
              <ul>
                <li>Use o Chrome em modo não-corporativo ou sem restrições</li>
                <li>
                  Verifique com administrador do sistema sobre políticas de
                  segurança
                </li>
                <li>
                  Como alternativa, você pode baixar o arquivo .hex e
                  transferi-lo manualmente
                </li>
              </ul>
              <button
                type="button"
                onClick={handleForceHexDownload}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Baixar arquivo .hex
              </button>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default MakecodeFrame;
